import React, { useState, useEffect } from 'react';
import {
	Badge,
	Box,
	Button,
	Card,
	Divider,
	Group,
	Stack,
	Text,
	Title,
	Tooltip,
} from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
	getEstablishment,
	getFeedingBargesList,
} from '../../../../../slices/thunks';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Tool } from 'feather-icons-react/build/IconComponents';

export const EstablishmentCardInfo = ({ id, height = 'auto' }) => {
	const dispatch = useDispatch();
	const [filteredFeedingBarges, setFilteredFeedingBarges] = useState([]);

	const selectorEstablishments = createSelector(
		state => state.Establishments,
		establishment => ({
			data: establishment.itemEstablishmentDetail,
			isLoading: establishment.isLoading,
			isSuccess: establishment.isSuccess,
			isError: establishment.isError,
		})
	);

	const { data, isLoading, isSuccess, isError } = useSelector(
		selectorEstablishments
	);

	const selectorFeedingBarges = createSelector(
		state => state.FeedingBarges,
		data => ({
			feeding_barges_list: data.itemFeedingBargesList,
		})
	);

	const { feeding_barges_list } = useSelector(selectorFeedingBarges);

	// Fetch establishment
	useEffect(() => {
		dispatch(getEstablishment(id));
	}, [id]);

	// Fetch feeding barges list
	useEffect(() => {
		dispatch(getFeedingBargesList({}));
	}, []);

	useEffect(() => {
		if (data && data.productive_cycle && feeding_barges_list) {
			const feedingBargesIds =
				data.productive_cycle.feeding_barges_ids || [];
			const filteredBarges = feeding_barges_list
				.filter(barge => feedingBargesIds.includes(barge._id))
				.map(barge => barge.name);

			setFilteredFeedingBarges(filteredBarges);
		}
	}, [data, feeding_barges_list]);

	const dateIni = data?.productive_cycle?.date_ini;
	const dateEnd = data?.productive_cycle?.date_end;

	const formattedDateIni = dateIni
		? format(parseISO(dateIni), 'MMMyyyy', { locale: es }).toUpperCase()
		: '-';
	const formattedDateEnd = dateEnd
		? format(parseISO(dateEnd), 'MMMyyyy', { locale: es }).toUpperCase()
		: '-';

	return (
		<Card
			radius='lg'
			shadow='xs'
			sx={{
				i: {
					fontSize: '0.8rem !important',
				},
			}}
		>
			<Stack spacing='sm'>
				<Text fw='bold'>Establecimiento</Text>
				<Stack spacing={8}>
					<Group spacing={6}>
						<Tooltip label='Nombre'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<Text fw='bold'>{data?.name?.toUpperCase() ?? '-'}</Text>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Estado'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<span className='fw-semibold float-end'>
							<span
								className='badge'
								style={{
									backgroundColor: data?.establishment_status?.bgcolor,
									color: data?.establishment_status?.txcolor,
								}}
							>
								{data?.establishment_status?.name?.toUpperCase() ?? '-'}
							</span>
						</span>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Tipo'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<Text>
							{data?.establishment_type?.name?.toUpperCase() ?? '-'}
						</Text>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Código SIEP'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<Text>{data?.code_siep ?? '-'}</Text>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Tipo de Propiedad'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<span
							className='badge'
							style={{
								backgroundColor: data?.property_type?.bgcolor,
								color: data?.property_type?.txcolor,
							}}
						>
							{data?.property_type?.name?.toUpperCase() ?? '-'}
						</span>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Empresa'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<Text>{data?.company?.name?.toUpperCase() ?? '-'}</Text>
					</Group>
					<Divider />
					<Group spacing={6}>
						<Tooltip label='Ciclo Productivo'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<Text>
							{data?.productive_cycle?.code?.toUpperCase() ?? '-'}
						</Text>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Fechas'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<Text>
							{formattedDateIni && formattedDateEnd
								? `${formattedDateIni} - ${formattedDateEnd}`
								: '-'}
						</Text>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Estado'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<span
							className='badge'
							style={{
								backgroundColor:
									data?.productive_cycle?.productive_cycle_status
										?.bgcolor,
								color: data?.productive_cycle?.productive_cycle_status
									?.txcolor,
							}}
						>
							{data?.productive_cycle?.productive_cycle_status?.name?.toUpperCase() ??
								'-'}
						</span>
					</Group>
					<Group spacing={6}>
						<Tooltip label='Etapa'>
							<i className='ri-file-search-line align-bottom text-muted'></i>
						</Tooltip>
						<span
							className='badge'
							style={{
								backgroundColor: data?.productive_cycle_stage?.bgcolor,
								color: data?.productive_cycle_stage?.txcolor,
							}}
						>
							{data?.productive_cycle_stage?.name?.toUpperCase() ?? '-'}
						</span>
					</Group>
				</Stack>
				<Divider />
				<Title order={2}>Artefactos</Title>
				<Group spacing={5}>
					{filteredFeedingBarges.length > 0 ? (
						filteredFeedingBarges.map((name, index) => (
							<Tooltip label={name}>
								<Button
									size='xs'
									variant='default'
									radius='lg'
									maw={100}
								>
									<Text truncate>{name}</Text>
								</Button>
							</Tooltip>
						))
					) : (
						<Text>No hay artefactos</Text>
					)}
				</Group>
			</Stack>
		</Card>
	);
};
